import type { QuestionnaireAutomationDraft } from '@/lib/preVisitQuestionnaireTiming';
import type {
  BookedCourseIntervalWarning,
  BookedCourseStatus,
  BookedStepStatus,
  BookedTreatmentCourse,
  BookedTreatmentCourseStep,
  IntervalUnit,
} from '@/types/treatmentCourses';

export type BookedStepDraft = {
  id: string;
  title: string;
  durationMinutes: number;
  startsAt: string | null;
  endsAt: string | null;
  practitionerIds: string[];
  resourceIds: string[];
  intervalValue: number;
  intervalUnit: IntervalUnit;
  intervalToleranceValue: number;
  intervalToleranceUnit: IntervalUnit;
  automations: QuestionnaireAutomationDraft[];
};

const INTERVAL_UNITS: IntervalUnit[] = ['days', 'weeks'];
const COURSE_STATUSES: BookedCourseStatus[] = ['active', 'completed', 'cancelled'];
const STEP_STATUSES: BookedStepStatus[] = [
  'unscheduled',
  'upcoming',
  'completed',
  'cancelled',
  'no_show',
];

function asIntervalUnit(value: unknown): IntervalUnit {
  return INTERVAL_UNITS.includes(value as IntervalUnit) ? (value as IntervalUnit) : 'days';
}

function asCourseStatus(value: unknown): BookedCourseStatus {
  return COURSE_STATUSES.includes(value as BookedCourseStatus)
    ? (value as BookedCourseStatus)
    : 'active';
}

function asStepStatus(value: unknown): BookedStepStatus {
  return STEP_STATUSES.includes(value as BookedStepStatus)
    ? (value as BookedStepStatus)
    : 'unscheduled';
}

function asStringArray(value: unknown): string[] {
  if (!Array.isArray(value)) return [];
  return value.filter((item): item is string => typeof item === 'string' && item.length > 0);
}

function asAutomation(value: unknown, index: number): QuestionnaireAutomationDraft | null {
  if (!value || typeof value !== 'object') return null;
  const row = value as Record<string, unknown>;
  const formsTemplateId = typeof row.forms_template_id === 'string' ? row.forms_template_id : '';
  if (!formsTemplateId) return null;
  const phase = row.phase === 'after_appointment' ? 'after_appointment' : 'before_appointment';
  return {
    id: typeof row.id === 'string' && row.id ? row.id : `auto-${index}-${formsTemplateId}`,
    forms_template_id: formsTemplateId,
    phase,
    offset_minutes: Number(row.offset_minutes) || 0,
    delay_after_booking_minutes: Number(row.delay_after_booking_minutes) || 0,
    is_active: row.is_active !== false,
    sort_order: Number(row.sort_order) || index,
  };
}

export function parseBookedTreatmentCourse(raw: unknown): BookedTreatmentCourse | null {
  if (!raw || typeof raw !== 'object') return null;
  const row = raw as Record<string, unknown>;
  if (typeof row.id !== 'string' || !row.id) return null;
  const stepsRaw = Array.isArray(row.steps) ? row.steps : [];
  const warningsRaw = Array.isArray(row.interval_warnings) ? row.interval_warnings : [];
  return {
    id: row.id,
    clinic_id: typeof row.clinic_id === 'string' ? row.clinic_id : '',
    patient_id: typeof row.patient_id === 'string' ? row.patient_id : '',
    template_id: typeof row.template_id === 'string' ? row.template_id : null,
    template_name: typeof row.template_name === 'string' ? row.template_name : '',
    status: asCourseStatus(row.status),
    booking_timing: row.booking_timing === 'allow_unbooked' ? 'allow_unbooked' : 'all_upfront',
    remaining_steps_bookable_by:
      row.remaining_steps_bookable_by === 'staff_and_patient' ? 'staff_and_patient' : 'staff_only',
    billing_mode: row.billing_mode === 'package' ? 'package' : 'per_step',
    package_price_cents:
      typeof row.package_price_cents === 'number' ? row.package_price_cents : null,
    template_snapshot: row.template_snapshot ?? null,
    ...(Object.prototype.hasOwnProperty.call(row, 'general_allowed_forms_template_ids')
      ? {
          general_allowed_forms_template_ids: asStringArray(row.general_allowed_forms_template_ids),
        }
      : {}),
    interval_warnings: warningsRaw
      .map((item): BookedCourseIntervalWarning | null => {
        if (!item || typeof item !== 'object') return null;
        const warning = item as Record<string, unknown>;
        if (typeof warning.step_id !== 'string') return null;
        return {
          step_id: warning.step_id,
          sort_order: Number(warning.sort_order) || 0,
          previous_step_id:
            typeof warning.previous_step_id === 'string' ? warning.previous_step_id : null,
          previous_starts_at:
            typeof warning.previous_starts_at === 'string' ? warning.previous_starts_at : null,
          this_starts_at:
            typeof warning.this_starts_at === 'string' ? warning.this_starts_at : null,
          window_start: typeof warning.window_start === 'string' ? warning.window_start : null,
          window_end: typeof warning.window_end === 'string' ? warning.window_end : null,
          is_broken: warning.is_broken === true,
        };
      })
      .filter((item): item is BookedCourseIntervalWarning => item !== null),
    steps: stepsRaw
      .map((item): BookedTreatmentCourseStep | null => {
        if (!item || typeof item !== 'object') return null;
        const step = item as Record<string, unknown>;
        if (typeof step.id !== 'string' || !step.id) return null;
        const automationsRaw = Array.isArray(step.automations) ? step.automations : [];
        return {
          id: step.id,
          sort_order: Number(step.sort_order) || 0,
          template_step_id:
            typeof step.template_step_id === 'string' ? step.template_step_id : null,
          appointment_id: typeof step.appointment_id === 'string' ? step.appointment_id : null,
          service_id: typeof step.service_id === 'string' ? step.service_id : null,
          service_name: typeof step.service_name === 'string' ? step.service_name : '',
          title: typeof step.title === 'string' ? step.title : null,
          interval_value: Number(step.interval_value) || 0,
          interval_unit: asIntervalUnit(step.interval_unit),
          interval_tolerance_value: Number(step.interval_tolerance_value) || 0,
          interval_tolerance_unit: asIntervalUnit(step.interval_tolerance_unit),
          duration_minutes: Number(step.duration_minutes) || 0,
          practitioner_mode:
            step.practitioner_mode === 'all' || step.practitioner_mode === 'selected'
              ? step.practitioner_mode
              : 'inherit',
          resource_mode: step.resource_mode === 'override' ? 'override' : 'inherit',
          questionnaire_mode:
            step.questionnaire_mode === 'inherit_plus' || step.questionnaire_mode === 'replace'
              ? step.questionnaire_mode
              : 'inherit',
          status: asStepStatus(step.status),
          starts_at: typeof step.starts_at === 'string' ? step.starts_at : null,
          ends_at: typeof step.ends_at === 'string' ? step.ends_at : null,
          practitioner_ids: asStringArray(step.practitioner_ids),
          resource_ids: asStringArray(step.resource_ids),
          automations: automationsRaw
            .map((auto, index) => asAutomation(auto, index))
            .filter((auto): auto is QuestionnaireAutomationDraft => auto !== null),
          allowed_forms_template_ids: asStringArray(step.allowed_forms_template_ids),
        };
      })
      .filter((item): item is BookedTreatmentCourseStep => item !== null)
      .sort((a, b) => a.sort_order - b.sort_order),
  };
}

export function courseProgress(course: BookedTreatmentCourse): {
  completed: number;
  total: number;
} {
  return {
    completed: course.steps.filter((step) => step.status === 'completed').length,
    total: course.steps.length,
  };
}

export function stepDisplayTitle(step: BookedTreatmentCourseStep, fallback: string): string {
  const title = step.title?.trim();
  if (title) return title;
  if (step.service_name.trim()) return step.service_name;
  return fallback;
}

export function draftFromBookedStep(step: BookedTreatmentCourseStep): BookedStepDraft {
  return {
    id: step.id,
    title: step.title?.trim() || step.service_name.trim() || '',
    durationMinutes: step.duration_minutes,
    startsAt: step.starts_at,
    endsAt: step.ends_at,
    practitionerIds: [...step.practitioner_ids],
    resourceIds: [...step.resource_ids],
    intervalValue: step.interval_value,
    intervalUnit: step.interval_unit,
    intervalToleranceValue: step.interval_tolerance_value,
    intervalToleranceUnit: step.interval_tolerance_unit,
    automations: step.automations.map((row) => ({ ...row })),
  };
}

function sortedIds(ids: readonly string[]): string {
  return [...ids].sort().join(',');
}

function automationFingerprint(draft: BookedStepDraft): string {
  return JSON.stringify(
    draft.automations
      .map((row) => ({
        forms_template_id: row.forms_template_id,
        phase: row.phase,
        offset_minutes: row.offset_minutes,
        delay_after_booking_minutes: row.delay_after_booking_minutes,
        is_active: row.is_active,
        sort_order: row.sort_order,
      }))
      .sort((a, b) => a.sort_order - b.sort_order || a.forms_template_id.localeCompare(b.forms_template_id)),
  );
}

export function bookedStepDraftChangeCount(
  original: BookedStepDraft,
  draft: BookedStepDraft,
): number {
  let count = 0;
  if ((original.title.trim() || '') !== (draft.title.trim() || '')) count += 1;
  if (original.durationMinutes !== draft.durationMinutes) count += 1;
  if ((original.startsAt ?? '') !== (draft.startsAt ?? '')) count += 1;
  if (original.intervalValue !== draft.intervalValue) count += 1;
  if (original.intervalUnit !== draft.intervalUnit) count += 1;
  if (original.intervalToleranceValue !== draft.intervalToleranceValue) count += 1;
  if (original.intervalToleranceUnit !== draft.intervalToleranceUnit) count += 1;
  if (sortedIds(original.practitionerIds) !== sortedIds(draft.practitionerIds)) count += 1;
  if (sortedIds(original.resourceIds) !== sortedIds(draft.resourceIds)) count += 1;
  if (automationFingerprint(original) !== automationFingerprint(draft)) count += 1;
  return count;
}

export function saveStepPayload(draft: BookedStepDraft): {
  title: string | null;
  duration_minutes: number;
  starts_at: string | null;
  ends_at: string | null;
  practitioner_ids: string[];
  resource_ids: string[];
  interval_value: number;
  interval_unit: IntervalUnit;
  interval_tolerance_value: number;
  interval_tolerance_unit: IntervalUnit;
  automations: Array<{
    forms_template_id: string;
    phase: QuestionnaireAutomationDraft['phase'];
    offset_minutes: number;
    delay_after_booking_minutes: number;
    is_active: boolean;
    sort_order: number;
  }>;
} {
  return {
    title: draft.title.trim() || null,
    duration_minutes: draft.durationMinutes,
    starts_at: draft.startsAt,
    ends_at: draft.endsAt,
    practitioner_ids: draft.practitionerIds,
    resource_ids: draft.resourceIds,
    interval_value: draft.intervalValue,
    interval_unit: draft.intervalUnit,
    interval_tolerance_value: draft.intervalToleranceValue,
    interval_tolerance_unit: draft.intervalToleranceUnit,
    automations: draft.automations.map((row, index) => ({
      forms_template_id: row.forms_template_id,
      phase: row.phase,
      offset_minutes: row.offset_minutes,
      delay_after_booking_minutes: row.delay_after_booking_minutes,
      is_active: row.is_active,
      sort_order: row.sort_order || index,
    })),
  };
}

export function brokenIntervalWarnings(
  course: BookedTreatmentCourse,
): BookedCourseIntervalWarning[] {
  return course.interval_warnings.filter((warning) => warning.is_broken);
}

export function canEditBookedCourse(status: BookedCourseStatus, canUpdate: boolean): boolean {
  return canUpdate && status === 'active';
}

export function canCancelBookedCourse(status: BookedCourseStatus, canUpdate: boolean): boolean {
  return canEditBookedCourse(status, canUpdate);
}

export function parseCancelTreatmentCourseResult(raw: unknown): {
  course: BookedTreatmentCourse;
  cancelledAppointmentIds: string[];
} | null {
  if (!raw || typeof raw !== 'object') return null;
  const row = raw as Record<string, unknown>;
  const course = parseBookedTreatmentCourse(row.course);
  if (!course) return null;
  const idsRaw = row.cancelled_appointment_ids;
  const cancelledAppointmentIds = Array.isArray(idsRaw)
    ? idsRaw.filter((id): id is string => typeof id === 'string' && id.length > 0)
    : [];
  return { course, cancelledAppointmentIds };
}

export function canBookBookedStep(status: BookedStepStatus): boolean {
  switch (status) {
    case 'unscheduled':
      return true;
    case 'upcoming':
    case 'completed':
    case 'cancelled':
    case 'no_show':
      return false;
    default: {
      const _exhaustive: never = status;
      return _exhaustive;
    }
  }
}

export function canCancelBookedStep(status: BookedStepStatus): boolean {
  switch (status) {
    case 'unscheduled':
    case 'upcoming':
      return true;
    case 'completed':
    case 'cancelled':
    case 'no_show':
      return false;
    default: {
      const _exhaustive: never = status;
      return _exhaustive;
    }
  }
}
